/**
 * Content language detection — read pages in their own language.
 *
 * The server's Kokoro voices speak 7 languages besides English; for other
 * languages the browser voice is used so text isn't read with an English voice.
 * Mirrors KOKORO_LANGUAGES in src/lib/tts-deepinfra.ts (keep in sync).
 */
export const NATURAL_VOICE_BY_LANG: Record<string, string> = {
  es: 'ef_dora',
  fr: 'ff_siwis',
  it: 'if_sara',
  pt: 'pf_dora',
  hi: 'hf_alpha',
  ja: 'jf_alpha',
  zh: 'zf_xiaobei',
};

/** Raw Kokoro voice ids look like "ef_dora"; the first letter is the language */
const KOKORO_ID = /^[abefhijpz][fm]_[a-z]+$/;

export function baseLang(code: string | null | undefined): string {
  return (code || '').trim().toLowerCase().split(/[-_]/)[0];
}

/** Language of the content: nearest [lang] on the element, else <html lang>, else English */
export function detectContentLanguage(el?: Element | null): string {
  const own = el?.closest?.('[lang]')?.getAttribute('lang');
  const doc = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
  return baseLang(own || doc) || 'en';
}

export type ReadingVoice =
  | { kind: 'default' }                                  // English: use the configured voice
  | { kind: 'natural'; voice: string; lang: string }     // server voice for that language
  | { kind: 'browser'; lang: string };                   // no server voice: browser speech

/**
 * Decide how to read content in `lang`.
 * A publisher who chose a non-English Kokoro voice explicitly keeps it.
 */
export function readingVoiceFor(lang: string, configuredVoice: string): ReadingVoice {
  const base = baseLang(lang);
  if (!base || base === 'en') return { kind: 'default' };
  if (KOKORO_ID.test(configuredVoice) && !configuredVoice.startsWith('a') && !configuredVoice.startsWith('b')) {
    return { kind: 'default' };
  }
  const voice = NATURAL_VOICE_BY_LANG[base];
  return voice ? { kind: 'natural', voice, lang: base } : { kind: 'browser', lang: base };
}
